import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import './DocumentationBrowser.css';

const DocumentationBrowser = () => {
  const [folder, setFolder] = useState(null);
  const [selectedFile, setSelectedFile] = useState(null);
  const [selectedContent, setSelectedContent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadingContent, setLoadingContent] = useState(false);
  const [error, setError] = useState(null);
  const [isConfigured, setIsConfigured] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState(null);
  const [isSearching, setIsSearching] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [expandedFolders, setExpandedFolders] = useState(new Set());

  useEffect(() => {
    loadDocumentationFolder();
  }, []);

  // Search functionality
  const performSearch = useCallback(async (query) => {
    if (!query || query.trim().length < 2) {
      setSearchResults(null);
      return;
    }
    
    setIsSearching(true);
    try {
      const response = await axios.get('/api/documentation/search', {
        params: { q: query.trim() },
        withCredentials: true
      });
      setSearchResults(response.data);
      setError(null);
    } catch (error) {
      console.error('Search failed:', error);
      setError('Search failed. Please try again.');
      setSearchResults(null);
    } finally {
      setIsSearching(false);
    }
  }, []);

  // Debounce search to avoid too many API calls
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      performSearch(searchQuery);
    }, 300);
    
    return () => clearTimeout(timeoutId);
  }, [searchQuery, performSearch]);

  const clearSearch = () => {
    setSearchQuery('');
    setSearchResults(null);
  };

  const loadDocumentationFolder = useCallback(async () => {
    setLoading(true);
    try {
      const response = await axios.get('/api/documentation/structure', { 
        withCredentials: true 
      });
      
      setFolder(response.data);
      setIsConfigured(true);
      setError(null);
    } catch (error) {
      console.error('Failed to load documentation structure:', error);
      setError('Failed to load documentation structure');
      setIsConfigured(false);
    } finally {
      setLoading(false);
    }
  }, []);

  const loadFileContent = async (file) => {
    if (file.isFile !== true) {
      // Expand/collapse folder
      setExpandedFolders(prev => {
        const newExpanded = new Set(prev);
        if (newExpanded.has(file.path)) {
          newExpanded.delete(file.path);
        } else {
          newExpanded.add(file.path);
        }
        return newExpanded;
      });
      return;
    }

    if (!file.path.toLowerCase().endsWith('.md')) {
      setError('Only markdown files (.md) are supported');
      return;
    }

    setLoadingContent(true);
    setSelectedFile(file);
    
    try {
      const response = await axios.get('/api/documentation/file', { 
        params: { path: file.path },
        withCredentials: true 
      });
      setSelectedContent(response.data);
      setError(null);
    } catch (error) {
      console.error('Failed to load file content:', error);
      setError('Failed to load file content');
      setSelectedContent(null);
    } finally {
      setLoadingContent(false);
    }
  };

  const renderFileTree = (items, level = 0) => {
    if (!items || typeof items !== 'object') return null;

    return Object.entries(items)
      .filter(([name, item]) => {
        // Skip if not an object with proper structure
        if (!item || typeof item !== 'object' || !item.path) return false;
return true;
      })
      .map(([name, item]) => {
        const isFile = item.isFile === true;
        const isExpanded = expandedFolders.has(item.path);
        const isSelected = selectedFile?.path === item.path;

        return (
          <div key={item.path} className="select-none">
            <div
              className={`flex items-center p-2 rounded cursor-pointer hover:bg-gray-100 ${
                isSelected ? 'bg-blue-50 text-blue-700' : 'text-gray-700'
              }`}
              style={{ paddingLeft: `${12 + level * 16}px` }}
              onClick={() => loadFileContent(item)}
            >
              {!isFile && (
                <svg 
                  className={`w-3 h-3 mr-2 transform transition-transform ${isExpanded ? 'rotate-90' : ''}`} 
                  fill="currentColor" 
                  viewBox="0 0 20 20"
                >
                  <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" />
                </svg>
              )}
              
              <span className="mr-2 text-sm">
                {isFile ? '📝' : '📁'}
              </span>
              
              <span className="flex-1 text-sm truncate" title={name}>
                {name}
              </span>
            </div>
            
            {!isFile && isExpanded && (
              <div>
                {renderFileTree(
                  Object.fromEntries(
                    Object.entries(item).filter(([key]) => key !== 'path' && key !== 'isFile')
                  ), 
                  level + 1
                )}
              </div>
            )}
          </div>
        );
      });
  };

  const renderSearchResults = () => {
    if (!searchResults) return null;
    
    if (searchResults.totalResults === 0) {
      return (
        <div className="p-4 text-center text-gray-500">
          <svg className="w-12 h-12 mx-auto mb-2 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
          </svg>
          <p>No results found for "{searchResults.query}"</p>
        </div>
      );
    }
    
    return (
      <div className="space-y-2">
        <div className="p-2 text-sm text-gray-600 border-b border-gray-200">
          Found {searchResults.totalResults} result(s) for "{searchResults.query}"
          <button 
            onClick={clearSearch}
            className="float-right text-blue-600 hover:text-blue-800"
          >
            Clear
          </button>
        </div>
        
        {searchResults.results.map((result, index) => (
          <div key={index} className="border border-gray-200 rounded-md">
            <div
              className="p-3 cursor-pointer hover:bg-gray-50"
              onClick={() => loadFileContent({ path: result.path, isFile: true })}
            >
              <div className="flex items-center mb-1">
                <span className="text-sm mr-2">📝</span>
                <span className="text-sm font-medium text-blue-600 hover:text-blue-800">
                  {result.filename}
                </span>
                {result.filenameMatch && (
                  <span className="ml-2 px-1.5 py-0.5 bg-yellow-100 text-yellow-800 text-xs rounded">
                    filename match
                  </span>
                )}
              </div>
              
              <div className="text-xs text-gray-500 mb-2">
                Path: {result.path}
              </div>
              
              {result.contentMatches > 0 && (
                <div className="text-xs text-gray-600 mb-2">
                  {result.contentMatches} content match(es)
                </div>
              )}
              
              {result.matches.length > 0 && (
                <div className="space-y-1">
                  {result.matches.slice(0, 2).map((match, matchIndex) => (
                    <div key={matchIndex} className="bg-gray-50 p-2 rounded text-xs">
                      <div className="text-gray-500 mb-1">Line {match.lineNumber}:</div>
                      <div className="font-mono whitespace-pre-wrap">{match.context}</div>
                    </div>
                  ))}
                  {result.matches.length > 2 && (
                    <div className="text-xs text-gray-500 text-center">
                      ... and {result.matches.length - 2} more match(es)
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        ))}
      </div>
    );
  };

  const renderMarkdownContent = () => {
    if (loadingContent) {
      return (
        <div className="flex items-center justify-center h-full">
          <div className="text-center">
            <div className="inline-block animate-spin rounded-full h-8 w-8 border-4 border-blue-400 border-t-transparent"></div>
            <p className="text-gray-500 mt-2">Loading document...</p>
          </div>
        </div>
      );
    }

    if (!selectedContent) {
      return (
        <div className="flex items-center justify-center h-full">
          <div className="text-center text-gray-500">
            <svg className="w-16 h-16 mx-auto text-gray-300 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
            <p>Select a markdown file to view its contents</p>
          </div>
        </div>
      );
    }

    return (
      <div className="p-6 prose max-w-none">
        <div 
          className="markdown-content"
          dangerouslySetInnerHTML={{ __html: selectedContent.html }}
        />
      </div>
    );
  };

  if (loading) {
    return (
      <div className="bg-white rounded-lg shadow-md p-6">
        <div className="text-center py-8">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-4 border-blue-400 border-t-transparent"></div>
          <p className="text-gray-500 mt-2">Loading documentation...</p>
        </div>
      </div>
    );
  }

  if (!isConfigured) {
    return (
      <div className="bg-white rounded-lg shadow-md p-6">
        <h2 className="text-xl font-semibold text-gray-900 mb-4 flex items-center">
          <svg className="w-6 h-6 mr-2 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
          </svg>
          SEEN Documentation
        </h2>
        <div className="text-center py-8">
          <svg className="w-16 h-16 mx-auto text-gray-300 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z" />
          </svg>
          <p className="text-gray-500 mb-4">Documentation not available</p>
          <button onClick={loadDocumentationFolder} className="inline-flex items-center px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600">
            <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
            </svg>
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow-md h-[700px] flex flex-col">
      {/* Header */}
      <div className="p-4 border-b border-gray-200 flex-shrink-0">
        <div className="flex justify-between items-center mb-3">
          <h2 className="text-xl font-semibold text-gray-900 flex items-center">
            <svg className="w-6 h-6 mr-2 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
            SEEN Documentation
          </h2>
          <div className="flex items-center space-x-2">
            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
              <span className="w-2 h-2 bg-green-400 rounded-full mr-1"></span>
              Local Docs
            </span>
            <button
              onClick={loadDocumentationFolder}
              className="p-2 text-gray-500 hover:text-gray-700 rounded-md hover:bg-gray-100"
              title="Refresh"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
              </svg>
            </button>
          </div>
        </div>

        {/* Error Display */}
        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-3 py-2 rounded-lg text-sm">
            {error}
          </div>
        )}
      </div>

      {/* Split Pane Content */}
      <div className="flex flex-1 overflow-hidden">
        {/* Left Sidebar */}
        <div className={`${sidebarCollapsed ? 'w-12' : 'w-96'} transition-all duration-300 border-r border-gray-200 flex flex-col bg-gray-50`}>
          {!sidebarCollapsed && (
            <>
              {/* Search */}
              <div className="p-3 border-b border-gray-200 bg-white">
                <div className="relative">
                  {isSearching ? (
                    <div className="absolute left-3 top-2.5 h-4 w-4 animate-spin">
                      <div className="h-4 w-4 border-2 border-blue-500 border-t-transparent rounded-full"></div>
                    </div>
                  ) : (
                    <svg className="absolute left-3 top-2.5 h-4 w-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                    </svg>
                  )}
                  <input
                    type="text"
                    placeholder="Search documentation..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="w-full pl-10 pr-10 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                  {searchQuery && (
                    <button
                      onClick={clearSearch}
                      className="absolute right-3 top-2.5 h-4 w-4 text-gray-400 hover:text-gray-600"
                    >
                      <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  )}
                </div>
              </div>
              
              {/* File Tree or Search Results */}
              <div className="flex-1 overflow-y-auto p-2">
{searchQuery && searchQuery.trim().length >= 2 ?
                  renderSearchResults() :
                  (folder && renderFileTree(folder))
                }
              </div>
            </>
          )}
          
          {/* Sidebar Toggle */}
          <div className="p-2 border-t border-gray-200 bg-white">
            <button
              onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
              className="p-2 text-gray-500 hover:text-gray-700 rounded-md hover:bg-gray-100 w-full"
              title={sidebarCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
            >
              <svg className={`w-4 h-4 mx-auto transform transition-transform ${sidebarCollapsed ? 'rotate-0' : 'rotate-180'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
            </button>
          </div>
        </div>

        {/* Main Content Area */}
        <div className="flex-1 flex flex-col overflow-hidden">
          {selectedFile && (
            <div className="p-4 border-b border-gray-200 bg-gray-50 flex-shrink-0">
              <div className="flex items-center justify-between">
                <div>
                  <h1 className="text-lg font-semibold text-gray-900">{selectedFile.path.split('/').pop()}</h1>
                  <p className="text-sm text-gray-500">
                    Path: {selectedFile.path}
                  </p>
                </div>
              </div>
            </div>
          )}
          
          <div className="flex-1 overflow-y-auto">
            {renderMarkdownContent()}
          </div>
        </div>
      </div>
    </div>
  );
};

export default DocumentationBrowser;